import React, { useEffect, useRef, useImperativeHandle } from 'react';

import 'mapbox-gl/dist/mapbox-gl.css';
import { elevationService } from '../../services/ElevationService';
import { MAP_CONSTANTS } from '../Mapbox/mapConstants';
import { updatePathSource, flyToPosition } from '../Mapbox/mapOperations';
import { setupAdditionalPathLayer } from '../Mapbox/mapSetupUtils';
import { LiveLineData, MapType, MarkerData } from '../Mapbox/mapTypes';
import { calculateGimbalOffset } from '../Mapbox/mapUtils';
import { updateDroneMarker, updateGimbalMarker } from '../Mapbox/markerOperations';
import { useMapInitialization } from '../Mapbox/useMapInitialization';
import { useUserDragTracking } from '../Mapbox/useUserDragTracking';

const DEFAULT_LINE_COLORS = ['#FF69B4', '#1E90FF'];

interface DualMapDroneViewProps {
  liveLines: LiveLineData[];
  lineColors?: string[];
  callsigns?: string[];
  followDeviceId?: string | null;
  onFollowChange?: (deviceId: string | null) => void;
  mapRef?: React.MutableRefObject<MapType | null>;
  customZoomControls?: boolean;
  initialZoom?: number;
  hideControls?: boolean;
  inPipMode?: boolean;
  staticMarkers?: { [id: string]: MarkerData };
  gimbalPixelOffset?: number;
}

const DualMapDroneView: React.FC<DualMapDroneViewProps> = ({
  liveLines,
  lineColors = DEFAULT_LINE_COLORS,
  callsigns,
  followDeviceId = null,
  onFollowChange,
  mapRef,
  customZoomControls = false,
  initialZoom = MAP_CONSTANTS.DEFAULT_ZOOM,
  hideControls = false,
  inPipMode = false,
  staticMarkers,
  gimbalPixelOffset
}) => {
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const targetZoomRef = useRef<number>(initialZoom);
  const createdTimeRef = useRef<number>(Date.now());
  const multiPathSetupRef = useRef<Set<string>>(new Set());
  const lastProcessedLengthsRef = useRef<Map<string, number>>(new Map());
  const clickListenersRef = useRef<Map<string, () => void>>(new Map());
  const followDeviceIdRef = useRef<string | null>(followDeviceId);
  const onFollowChangeRef = useRef(onFollowChange);

  useEffect(() => {
    followDeviceIdRef.current = followDeviceId;
  }, [followDeviceId]);

  useEffect(() => {
    onFollowChangeRef.current = onFollowChange;
  }, [onFollowChange]);

  const { map, markerManagerRef, mapLoaded } = useMapInitialization(
    mapContainer,
    initialZoom,
    customZoomControls,
    hideControls
  );

  const flyToEnabledRef = useUserDragTracking(map, mapLoaded);

  useEffect(() => {
    targetZoomRef.current = initialZoom;
  }, [initialZoom]);

  // Set up additional path layers for each liveLine
  useEffect(() => {
    const mapInstance = map.current;
    if (!mapInstance || !mapLoaded || liveLines.length === 0) return;

    liveLines.forEach((_, idx) => {
      const sourceId = `path-multi-${idx}`;
      if (!multiPathSetupRef.current.has(sourceId)) {
        const color = lineColors[idx % lineColors.length];
        setupAdditionalPathLayer(mapInstance, sourceId, color);
        multiPathSetupRef.current.add(sourceId);
      }
    });
  }, [map, mapLoaded, liveLines, lineColors]);

  // Attach click listeners to drone markers
  useEffect(() => {
    if (!markerManagerRef.current || !mapLoaded) return;

    const markerManager = markerManagerRef.current;
    const clickListeners = clickListenersRef.current;

    liveLines.forEach((ll) => {
      if (ll.lines.length === 0) return;

      const marker = markerManager.getMarker(ll.deviceId);
      if (marker && !clickListeners.has(ll.deviceId)) {
        const el = marker.getElement();
        el.style.cursor = 'pointer';
        const handler = (): void => {
          const currentFollow = followDeviceIdRef.current;
          const newFollow = currentFollow === ll.deviceId ? null : ll.deviceId;
          onFollowChangeRef.current?.(newFollow);
        };
        el.addEventListener('click', handler);
        clickListeners.set(ll.deviceId, handler);
      }
    });

    return () => {
      clickListeners.forEach((handler, deviceId) => {
        const marker = markerManager.getMarker(deviceId);
        if (marker) {
          marker.getElement().removeEventListener('click', handler);
        }
      });
      clickListeners.clear();
    };
  }, [markerManagerRef, liveLines, mapLoaded]);

  // Update paths and markers via RAF loop
  useEffect(() => {
    if (!map.current || !mapLoaded || liveLines.length === 0) return;

    let animFrameId: number | null = null;

    const update = (): void => {
      const mapInstance = map.current;
      const markerManager = markerManagerRef.current;
      if (!mapInstance || !markerManager) return;

      liveLines.forEach((ll, idx) => {
        if (ll.lines.length === 0) return;

        const sourceId = `path-multi-${idx}`;
        const currentPosition = ll.lines[ll.lines.length - 1];
        const prevLength = lastProcessedLengthsRef.current.get(sourceId) ?? 0;

        // Update drone marker
        if (ll.canUpdateMarkerLocation) {
          updateDroneMarker(
            mapInstance,
            markerManager,
            ll.deviceId,
            currentPosition,
            ll.marker || null,
            ll.markerAngle,
            ll.lines
          );

          // Update gimbal marker
          updateGimbalMarker(
            mapInstance,
            markerManager,
            ll.deviceId,
            currentPosition,
            ll.gimbal || null,
            ll.gimbalAngle,
            ll.lines,
            inPipMode,
            gimbalPixelOffset
          );
        }

        // Update path when new points arrive
        if (ll.lines.length > prevLength) {
          lastProcessedLengthsRef.current.set(sourceId, ll.lines.length);
          updatePathSource(map.current, ll.lines, inPipMode, sourceId);
        }

        // Follow this drone if selected
        if (followDeviceIdRef.current === ll.deviceId && ll.lines.length > prevLength) {
          flyToPosition(
            map.current,
            currentPosition,
            targetZoomRef,
            createdTimeRef,
            undefined,
            flyToEnabledRef.current
          );
        }
      });

      // Auto-fit bounds when not following any drone
      if (!followDeviceIdRef.current) {
        const allPositions = liveLines
          .filter((ll) => ll.lines.length > 0)
          .map((ll) => ll.lines[ll.lines.length - 1]);

        if (allPositions.length >= 2 && map.current) {
          const lngs = allPositions.map((p) => p[0]);
          const lats = allPositions.map((p) => p[1]);
          const sw: [number, number] = [Math.min(...lngs), Math.min(...lats)];
          const ne: [number, number] = [Math.max(...lngs), Math.max(...lats)];

          const bounds = map.current.getBounds();
          if (bounds && (!bounds.contains(sw) || !bounds.contains(ne))) {
            map.current.fitBounds([sw, ne], { padding: 80, maxZoom: 18, duration: 1000 });
          }
        }
      }

      animFrameId = requestAnimationFrame(update);
    };

    animFrameId = requestAnimationFrame(update);

    return () => {
      if (animFrameId !== null) cancelAnimationFrame(animFrameId);
    };
  }, [map, markerManagerRef, liveLines, mapLoaded, inPipMode, gimbalPixelOffset]);

  // Render static markers (home positions)
  useEffect(() => {
    const mapInstance = map.current;
    if (!mapInstance || !mapLoaded || !markerManagerRef.current || !staticMarkers) return;
    const manager = markerManagerRef.current;

    Object.values(staticMarkers).forEach((markerData) => {
      if (manager.hasMarker(markerData.id)) {
        manager.updateMarker(markerData.id, markerData.position);
      } else {
        manager.createMarker(markerData.id, markerData.image, markerData.position, mapInstance);
      }
    });
  }, [staticMarkers, mapLoaded, map, markerManagerRef]);

  // Keep gimbal markers positioned correctly on map move/zoom
  useEffect(() => {
    const mapInstance = map.current;
    if (!mapInstance || !mapLoaded || !markerManagerRef.current) return;

    const updateGimbalPositions = (): void => {
      const manager = markerManagerRef.current;
      if (!mapInstance || !manager) return;

      liveLines.forEach((ll) => {
        const gimbalMarkerId = `${ll.deviceId}_gimbal`;
        const gimbalMarker = manager.getMarker(gimbalMarkerId);

        if (gimbalMarker && ll.lines.length > 0 && ll.gimbalAngle !== undefined) {
          const dronePos = ll.lines[ll.lines.length - 1];
          const gimbalPos = calculateGimbalOffset(dronePos, ll.gimbalAngle, mapInstance, inPipMode, gimbalPixelOffset);
          manager.updateMarker(gimbalMarkerId, gimbalPos);
        }
      });
    };

    mapInstance.on('move', updateGimbalPositions);
    mapInstance.on('zoom', updateGimbalPositions);

    return () => {
      mapInstance.off('move', updateGimbalPositions);
      mapInstance.off('zoom', updateGimbalPositions);
    };
  }, [liveLines, mapLoaded, inPipMode, map, markerManagerRef, gimbalPixelOffset]);

  useImperativeHandle<MapType, MapType>(
    mapRef,
    (): MapType => ({
      getElevation: async ([longitude, latitude]: [number, number]): Promise<number | '--'> => {
        if (!map.current || !mapLoaded) return '--';

        try {
          const elevation = await elevationService.getElevation(latitude, longitude);

          if (
            elevation !== null &&
            elevation !== undefined &&
            !Number.isNaN(elevation) &&
            Number.isFinite(elevation)
          ) {
            return Math.round(elevation);
          }

          return '--';
        } catch (error) {
          console.error('Failed to get elevation:', error);
          return '--';
        }
      },
      resize: (): void => {
        if (map.current && mapLoaded) {
          map.current.resize();
        }
      },
      getZoom: (): number | undefined => {
        if (!map.current || !mapLoaded) return undefined;
        return map.current.getZoom();
      }
    })
  );

  return (
    <div className='w-full h-full flex relative'>
      <style>
        {`
        .mapboxgl-ctrl-logo, .mapboxgl-ctrl-attrib {
          display: none !important;
        }
      `}
      </style>
      <div className='w-full h-full' ref={mapContainer} />
    </div>
  );
};

export default React.memo(DualMapDroneView);
